import { useInfiniteQuery } from '@tanstack/react-query'
import { Filter, type Sort } from 'mongodb'
import type { Listing } from '~/models'

type FetchListingsResponse = {
  success: boolean
  message?: string

  listings: Listing[]
  count: number
  nextOffset: number
}

async function fetchListings({
  find,
  sort,
  project,
  limit,
  offset,
  signal,
}: {
  find: Filter<Listing>
  sort?: Sort
  project?: Record<string, number>
  limit?: number
  offset?: number
  signal?: AbortSignal
}): Promise<FetchListingsResponse> {
  const response = await fetch('/api/map-listings', {
    body: JSON.stringify({
      find,
      sort,
      project,
      limit,
      offset,
    }),
    method: 'POST',
    signal,
  })
  const result = await response.json()
  if (!result.success) {
    console.error('Error loading listings', result.error)
    throw new Error(`There was a problem fetching results: ${result.error}`)
  }
  return result
}

export function useListingsQuery({
  key,
  sort,
  find,
  limit,
  project,
  // offset,
  enabled = true,
  refetchOnWindowFocus = false,
  refetchOnMount = false,
  refetchInterval,
}: {
  key: string[]
  find: Record<string, any>
  sort?: Sort
  limit?: number
  project?: Record<string, any>
  enabled?: boolean
  refetchOnWindowFocus?: boolean
  refetchOnMount?: boolean
  refetchInterval?: number
}) {
  const listingsQuery = useInfiniteQuery({
    // retry(failureCount) // TODO Add retry warning after draw implemented
    queryKey: key,
    queryFn: ({ signal, pageParam = 0 }) => {
      return fetchListings({
        find,
        sort,
        project,
        limit,
        offset: pageParam,
        signal,
      })
    },
    enabled,
    refetchOnWindowFocus,
    refetchOnMount,
    // keepPreviousData: true,
    refetchInterval,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextOffset,
  })

  return {
    isLoading:
      listingsQuery.isLoading ||
      listingsQuery.isFetching ||
      listingsQuery.isRefetching ||
      (listingsQuery.isPending && enabled),
    // ||listingsQuery.isFetching,
    isFetching: listingsQuery.isFetching,
    // || listingsQuery.isRefetching,
    isRefetching: listingsQuery.isRefetching,
    isError: listingsQuery.isError,
    listings: listingsQuery.data?.pages?.map((p) => p.listings).flat() || [],
    pages: listingsQuery.data?.pages || [],
    count: listingsQuery.data?.pages?.[0].count,
    refetch: listingsQuery.refetch,
    fetchNextPage: listingsQuery.fetchNextPage,
    hasNextPage: listingsQuery.hasNextPage,
    hasPreviousPage: listingsQuery.hasPreviousPage,
    isFetchingNextPage: listingsQuery.isFetchingNextPage,
  }
}
